<template>
  <b-card>
    <b-row class="position-relative">
      <b-col md="12">
        <div>
          <div class="d-flex align-items-center justify-content-lg-start">
            <Button
              v-if="showBackButton && actionType === 'save' || 'add'"
              :label="$t('List page')"
              size="md"
              icon="ArrowLeftIcon"
              show-icon
              class="mb-1"
              @click="backNavigationAction"
            />
          </div>
          <validation-observer ref="glPostingRules">
            <b-row>

              <b-col md="6">
                <SelectField
                  id="gl-to-debit"
                  v-model="glToDebit.amountToDebit"
                  :label="$t('GL to Debit')"
                  :placeholder="$t('Select')"
                  :options="glAccountOptions"
                  :reduce="option => option.value"
                  rules="required"
                  :error-name="$t('Gl to Debit')"
                  item="label"
                  :set-selected="setGLToDebitAmount"
                  :loading="isLoadingGlAccount"
                />
              </b-col>

              <b-col md="6">
                <SelectField
                  id="gl-to-credit"
                  v-model="glToCredit.amountToCredit"
                  :reduce="option => option.value"
                  :label="$t('GL to Credit')"
                  :placeholder="$t('Select')"
                  :options="glAccountOptions"
                  :error-name="$t('GL to Credit')"
                  rules="required"
                  item="label"
                  :set-selected="setGLToCreditAmount"
                  :loading="isLoadingGlAccount"
                />
              </b-col>

              <b-col md="6">
                <InputField
                  id="gl-to-credit"
                  v-model="glToDebit.accountNumber"
                  :label="$t('GL to Debit Account Number')"
                  type="text"
                  readonly
                />
              </b-col>

              <b-col md="6">
                <InputField
                  id="account-number"
                  v-model="glToCredit.accountNumber"
                  :label="$t('GL to Credit Account Number')"
                  type="text"
                  readonly
                />
              </b-col>

              <b-col md="6">
                <SelectField
                  id="gl-to-debit"
                  v-model="glToDebit.branchCode"
                  :reduce="option => option.value"
                  :label="$t('Branch')"
                  :placeholder="$t('Select')"
                  :options="branchOptions"
                  rules="required"
                  :error-name="$t('Branch to Debit')"
                  item="label"
                  :set-selected="setBranchCode"
                  :loading="isLoadingBranches"
                />
              </b-col>

              <b-col md="6">
                <SelectField
                  id="gl-to-credit"
                  v-model="glToCredit.branchCode"
                  :reduce="option => option.value"
                  :label="$t('Branch')"
                  :placeholder="$t('Select')"
                  :options="branchOptions"
                  rules="required"
                  :error-name="$t('Branch to Credit')"
                  item="label"
                  :loading="isLoadingBranches"
                />
              </b-col>

              <b-col md="6">
                <SelectField
                  id="entry-code-debit"
                  v-model="debitEntryCode"
                  :reduce="option => option"
                  :label="$t('Sort Code')"
                  :placeholder="$t('Select')"
                  :options="debitEntryCodeOptions"
                  :error-name="$t('Sort Code')"
                  rules="required"
                  item="label"
                />
              </b-col>

              <b-col md="6">
                <SelectField
                  id="entry-code-credit"
                  v-model="creditEntryCode"
                  :reduce="option => option"
                  :label="$t('Sort Code')"
                  :placeholder="$t('Select')"
                  :options="creditEntryCodeOptions"
                  :error-name="$t('Sort Code')"
                  rules="required"
                  item="label"
                />
              </b-col>

            </b-row>

            <b-row>
              <b-col md="6">
                <InputNumberField
                  id="amount"
                  v-model="depositData.amount"
                  :label="$t('Amount')"
                  :placeholder="$t('Enter amount')"
                  :error-name="$t('Amount')"
                  rules="required|positive"
                />
              </b-col>
              <b-col md="6">
                <InputField
                  id="instrument-no"
                  v-model="depositData.instrumentNumber"
                  :label="$t('Instrument No')"
                  :placeholder="$t('Enter instrument no')"
                  type="text"
                  rules="required"
                  :error-name="$t('Instrument No')"
                />
              </b-col>

              <b-col md="6">
                <InputField
                  id="naration"
                  v-model="depositData.debitNarration"
                  :label="$t('Debit Narration')"
                  :placeholder="$t('Enter Debit narration')"
                  type="text"
                  rules="required"
                  error-name="naration"
                  @input="setNarration"
                />
              </b-col>
              <b-col md="6">
                <InputField
                  id="naration"
                  v-model="depositData.creditNarration"
                  :label="$t('Credit Narration')"
                  :placeholder="$t('Enter Credit narration')"
                  type="text"
                  rules="required"
                  error-name="naration"
                />
              </b-col>
            </b-row>
            <div class="text-center mt-5">
              <Button
                :label="$t('Post')"
                :loading="isLoading"
                :disabled="isDisabled"
                size="lg"
                @click="handleSubmit"
              />
            </div>
          </validation-observer>

        </div>
      </b-col>
    </b-row>

    <FullScreenSpinner :show="showSpinner" />
  </b-card>
</template>

<script>

import {
  BCard, BRow, BCol,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import { heightFade } from '@core/directives/animations'
import { required, positive } from '@validations'
import { ValidationObserver } from 'vee-validate'
import moment from 'moment'
import { v4 as uuidv4 } from 'uuid'
import { sweetAlert } from '@core/mixins/sweetAlert'
import { mapActions, mapGetters } from 'vuex'
import { toKobo, toAmount } from '@/helpers/formatAmount'
import FullScreenSpinner from '@/views/components/spinner/FullScreenSpinner.vue'
import InputField from '../../../../forms/InputField.vue'
import SelectField from '../../../../forms/SelectField.vue'
import Button from '../../../../components/button/Button.vue'
import InputNumberField from '../../../../forms/InputNumberField.vue'
import { postingPermissions } from '../../../../../helpers/permissions'

const today = new Date()
const dateTime = moment(today).format()

export default {
  components: {
    BRow,
    BCol,
    InputField,
    Button,
    SelectField,
    InputNumberField,
    BCard,
    FullScreenSpinner,
    ValidationObserver,
  },
  directives: {
    Ripple,
    'height-fade': heightFade,
  },
  mixins: [sweetAlert],
  data() {
    return {
      isLoading: false,
      isLoadingBranches: false,
      isLoadingGlAccount: false,
      isDisabled: false,
      showFormErrorAlert: false,
      showCustomerInfo: false,
      showBackButton: false,
      customerList: [],
      required,
      positive,
      showSpinner: false,
      sortDesc: false,
      currentPage: 1,
      selected: [],
      perPage: 10,
      glAccountOptions: [],
      glToDebit: {
        amountToDebit: null,
        branchCode: null,
        accountNumber: '',
        accountName: '',
      },
      glToCredit: {
        amountToCredit: null,
        branchCode: null,
        accountNumber: '',
        accountName: '',
      },
      debitEntryCode: 'D-JRNL',
      creditEntryCode: 'C-JRNL',
      debitEntryCodeOptions: ['D-CSHD', 'D-CHQD1A', 'D-CHQD1B', 'D-CHQW', 'D-CSHW', 'D-JRNL', 'D-BCSH', 'D-SCSH', 'D-IBRH', 'D-LTRF'],
      creditEntryCodeOptions: ['C-CSHD', 'C-CHQD1A', 'C-CHQD1B', 'C-CHQW', 'C-CSHW', 'C-JRNL', 'C-BCSH', 'C-SCSH', 'C-IBRH', 'C-LTRF'],
      depositData: {
        accountNumber: '',
        amount: '',
        recordType: 'Debit',
        entryCode: '',
        debitNarration: '',
        creditNarration: '',
        instrumentNumber: '',
        branchCode: '',
      },
      postingFormData: {
        postingData: {
          realDate: dateTime,
          postingType: 'JournalPosting',
          initiatedBy: '',
        },
      },
      posting: [],
      branchOptions: [],
    }
  },
  computed: {
    ...mapGetters({
      userDetails: 'auth/getCurrentUser',
    }),
  },
  mounted() {
    this.getAllGLAccount()
    this.fetchBranches()
    this.depositData.instrumentNumber = uuidv4()
  },
  methods: {
    ...mapActions({
      getBranch: 'operations/getBranchList',
      getGLAccount: 'account/getAllGLAccount',
      getGlAccountNameByCode: 'account/getGlAccountNameByCode',
      postCashDeposit: 'posting/postingAction',
    }),
    setNarration() {
      this.depositData.creditNarration = this.depositData.debitNarration
    },
    async setGLToDebitAmount(selectedValue) {
      this.depositData.accountNumber = selectedValue
      this.glToDebit.accountNumber = selectedValue
      const response = await this.getGlAccountNameByCode({ code: selectedValue, permission: postingPermissions.SINGLE_JOURNAL_POSTING })
      const { data } = await response
      if (data.data !== null) {
        this.glToDebit.accountName = { ...data.data.map(item => item.name) }
      }
      return selectedValue
    },
    async setGLToCreditAmount(selectedValue) {
      this.glToCredit.amountToCredit = selectedValue
      this.glToCredit.accountNumber = selectedValue
      const response = await this.getGlAccountNameByCode({ code: selectedValue, permission: postingPermissions.SINGLE_JOURNAL_POSTING })
      const { data } = await response
      if (data.data !== null) {
        this.glToCredit.accountName = { ...data.data.map(item => item.name) }
      }
    },
    setBranchCode(selectedValue) {
      this.glToDebit.branchCode = selectedValue
    },
    async getAllGLAccount() {
      try {
        this.isLoadingGlAccount = true
        const response = await this.getGLAccount({ permission: 'SINGLE_JOURNAL_POSTING' })
        const { data, status } = response
        if (status === 200 && data.data) {
          this.glAccountOptions = data.data.filter(item => item.status === 'Active')
            .reduce((acc, curr) => [...acc, { label: `${curr.name} - ${curr.code}`, value: curr.code.toString() }],

              // eslint-disable-next-line no-nested-ternary
              []).sort((a, b) => (a.label === b.label ? 0 : a.label < b.label ? -1 : 1))
        }
      } catch (error) {
        this.alertError({ text: error })
      } finally {
        this.isLoadingGlAccount = false
      }
    },
    async handleSubmit() {
      this.$refs.glPostingRules.validate().then(success => {
        if (success) {
          const areYouSure = this.$t('Are you sure you want to initiate a Single GL of')
          this.confirm(this.glPostingAction, { text: `${areYouSure} ${toAmount(this.depositData.amount)} ?` })
        }
      })
    },
    async glPostingAction() {
      try {
        const postingApprovalModel = {
          amount: Number(toKobo(this.depositData.amount).toFixed()),
          initiatedBy: '',
          approvedBy: '',
          postingType: 'JournalPosting',
        }
        this.postingFormData.postingApprovalModel = postingApprovalModel
        this.postingFormData.postingData.initiatedBy = this.userDetails.email
        this.postingFormData.postingEntry = [
          {
            accountNumber: this.glToDebit.accountNumber,
            accountName: this.glToDebit.accountName[0],
            amount: Number(toKobo(this.depositData.amount).toFixed()),
            recordType: 'Debit',
            narration: this.depositData.debitNarration,
            instrumentNumber: this.depositData.instrumentNumber,
            entryCode: this.debitEntryCode,
            branchCode: this.glToDebit.branchCode,
          },
          {
            accountNumber: this.glToCredit.accountNumber,
            accountName: this.glToCredit.accountName[0],
            amount: Number(toKobo(this.depositData.amount).toFixed()),
            recordType: 'Credit',
            narration: this.depositData.creditNarration,
            instrumentNumber: this.depositData.instrumentNumber,
            entryCode: this.creditEntryCode,
            branchCode: this.glToCredit.branchCode,
          },
        ]
        this.isLoading = true
        this.isDisabled = true
        const postingData = this.postingFormData
        const response = await this.postCashDeposit({ postingData, permission: postingPermissions.SINGLE_JOURNAL_POSTING })
        const { status, data } = await response
        if (status === 200 && data.isSuccessful) {
          this.resetInputField()
          const successText = this.$t('Your Single GL request was successful. Single GL reference ID is')
          this.alertSuccess({ text: `${successText} ${data.referenceNumber}` })
          this.$router.push({ name: 'gl-posting-list' })
        }
        if (status === 200 && !data.isSuccessful) {
          this.isLoading = false
          this.isDisabled = false
          const failedText = this.$t('Your Single GL  request failed. Reason:')
          this.alertError({ text: `${failedText} ${data.message}` })
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isLoading = false
        this.isDisabled = false
      }
    },
    async fetchBranches() {
      try {
        this.isLoadingBranches = true
        const response = await this.getBranch({
          search: '',
          page: 1,
          limit: 1000,
          permission: 'SINGLE_JOURNAL_POSTING',
        })
        const { status, data } = response
        if (status === 200) {
          this.branchOptions = data.data.filter(item => item.status === 'Active').map(item => ({
            label: item.name, value: item.code,
          // eslint-disable-next-line no-nested-ternary
          })).sort((a, b) => (a.label === b.label ? 0 : a.label < b.label ? -1 : 1))
        }
      } catch (error) {
        this.alertError({ text: error })
      } finally {
        this.isLoadingBranches = false
      }
    },
    resetInputField() {
      this.depositData = { ...null }
      this.customer = { ...null }
    },
    backNavigationAction() {
      this.$router.push({
        name: 'gl-posting-list',
      })
    },
  },
}
</script>
<style scoped>
 [dir] .demo-inline-spacing > *, .custom-control , .custom-control-inline, .custom-checkbox {
    margin-top: 0rem !important;
}
</style>
